'use client'

import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import toast from 'react-hot-toast'
import { authClient } from '@/clients/better-auth'
import { AppUrls } from '@/enums'
import { LinkWrapper } from '@/components/common/LinkWrapper'
import { PageWrapper } from '@/components/common/PageWrapper'
import { CenteredContent } from '@/components/app/CenteredContent'
import { AppButton } from '@/components/ui/AppButton'

type FormData = {
    email: string
}

export default function ForgetPasswordPage() {
    const router = useRouter()
    const form = useForm<FormData>({
        defaultValues: { email: '' },
    })

    const mutation = useMutation({
        mutationFn: async (values: FormData) => {
            if (!values.email.includes('@')) throw new Error('Please enter a valid email address')
            return authClient.forgetPassword({
                email: values.email,
                redirectTo: AppUrls.RESET_PASSWORD,
            })
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to send reset link')
        },
        onSuccess: () => {
            toast.success('Reset link sent to your email')
            router.push(AppUrls.SIGN_IN)
        },
    })

    return (
        <PageWrapper>
            <CenteredContent>
                <h2 className="mb-2 text-2xl font-semibold">Forget password</h2>
                <p className="mb-6 text-default/60">Enter your email to reset your password</p>
                <form onSubmit={form.handleSubmit((values) => mutation.mutate(values))} className="flex w-full flex-col gap-4">
                    <div>
                        <label className="mb-1 block">Email</label>
                        <input
                            type="email"
                            {...form.register('email', {
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Please enter a valid email address',
                                },
                            })}
                            placeholder="your@email.com"
                            className="w-full rounded-lg border border-default/10 bg-default/5 p-2"
                        />
                        {form.formState.errors.email && <p className="mt-1 text-sm text-red-500">{form.formState.errors.email.message}</p>}
                    </div>
                    <AppButton type="submit" loading={mutation.isPending} loadingText="Sending..." disabled={!form.formState.isValid}>
                        Send Reset Link
                    </AppButton>
                    <LinkWrapper href={AppUrls.SIGN_IN} className="mx-auto hover:underline">
                        <p>Back to sign in</p>
                    </LinkWrapper>
                </form>
            </CenteredContent>
        </PageWrapper>
    )
}
